import { supabase } from '../supabase';
import type { Database } from '../types';

type CmsSection = Database['public']['Tables']['cms_sections']['Row'];
type CmsService = Database['public']['Tables']['cms_services']['Row'];
type CmsTeam = Database['public']['Tables']['cms_team']['Row'];
type CmsFooterLinks = Database['public']['Tables']['cms_footer_links']['Row'];

export const cmsApi = {
  // Expose supabase client for diagnostics
  supabase,

  // Sections
  async getSections() {
    console.log('CMS API: Getting all sections');
    const { data, error } = await supabase
      .from('cms_sections')
      .select('*')
      .order('type');

    if (error) {
      console.error('CMS API: Error getting sections:', error);
    } else {
      console.log('CMS API: Got sections:', data?.length || 0);
    }

    return { data, error };
  },

  async getSection(type: string) {
    console.log('CMS API: Getting section:', type);
    const { data, error } = await supabase
      .from('cms_sections')
      .select('*')
      .eq('type', type)
      .maybeSingle();

    if (error) {
      console.error('CMS API: Error getting section:', type, error);
    } else {
      console.log('CMS API: Got section:', type, data);
    }

    return { data, error };
  },

  async upsertSection(section: Partial<CmsSection>) {
    console.log('CMS API: Upserting section:', section.type);
    const { data, error } = await supabase
      .from('cms_sections')
      .upsert(section, { 
        onConflict: 'type',
        ignoreDuplicates: false 
      })
      .select()
      .single();

    if (error) {
      console.error('CMS API: Error upserting section:', error);
    } else {
      console.log('CMS API: Upserted section successfully:', data);
    }

    return { data, error };
  },

  // Services
  async getServices() {
    console.log('CMS API: Getting all services');
    const { data, error } = await supabase
      .from('cms_services')
      .select('*')
      .order('order_index', { nullsFirst: false });

    if (error) {
      console.error('CMS API: Error getting services:', error);
    } else {
      console.log('CMS API: Got services:', data?.length || 0);
    }

    return { data, error };
  },

  async getService(id: string) {
    console.log('CMS API: Getting service:', id);
    const { data, error } = await supabase
      .from('cms_services')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('CMS API: Error getting service:', id, error);
    } else {
      console.log('CMS API: Got service:', id, data);
    }

    return { data, error };
  },

  async upsertService(service: Partial<CmsService>) {
    console.log('CMS API: Upserting service:', service.id);
    const { data, error } = await supabase
      .from('cms_services')
      .upsert(service, { 
        onConflict: 'id',
        ignoreDuplicates: false 
      })
      .select()
      .single();

    if (error) {
      console.error('CMS API: Error upserting service:', error);
    } else {
      console.log('CMS API: Upserted service successfully:', data);
    }

    return { data, error };
  },

  async deleteService(id: string) {
    console.log('CMS API: Deleting service:', id);
    const { data, error } = await supabase
      .from('cms_services')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('CMS API: Error deleting service:', error);
    } else {
      console.log('CMS API: Deleted service successfully:', id);
    }

    return { data, error };
  },

  // Team
  async getTeamMembers() {
    console.log('CMS API: Getting team members');
    const { data, error } = await supabase
      .from('cms_team')
      .select('*')
      .order('created_at');

    if (error) {
      console.error('CMS API: Error getting team members:', error);
    } else {
      console.log('CMS API: Got team members:', data?.length || 0);
    }

    return { data, error };
  },

  async getTeamMember(id: string) {
    console.log('CMS API: Getting team member:', id);
    const { data, error } = await supabase
      .from('cms_team')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('CMS API: Error getting team member:', id, error);
    } else {
      console.log('CMS API: Got team member:', id, data);
    }

    return { data, error };
  },

  async createTeamMember(member: Omit<CmsTeam, 'id' | 'created_at' | 'updated_at'>) {
    console.log('CMS API: Creating team member:', member.name);
    const { data, error } = await supabase
      .from('cms_team')
      .insert(member)
      .select()
      .single();

    if (error) {
      console.error('CMS API: Error creating team member:', error);
    } else {
      console.log('CMS API: Created team member successfully:', data);
    }

    return { data, error };
  },

  async updateTeamMember(id: string, member: Partial<CmsTeam>) {
    console.log('CMS API: Updating team member:', id);
    const { data, error } = await supabase
      .from('cms_team')
      .update(member)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('CMS API: Error updating team member:', error);
    } else {
      console.log('CMS API: Updated team member successfully:', data);
    }

    return { data, error };
  },

  async upsertTeamMember(member: Partial<CmsTeam>) {
    console.log('CMS API: Upserting team member:', member.name);
    // If no id is provided, create a new member
    if (!member.id) {
      return this.createTeamMember(member as Omit<CmsTeam, 'id' | 'created_at' | 'updated_at'>);
    }

    // If id is provided, update existing member
    const { data, error } = await supabase
      .from('cms_team')
      .upsert(member, { 
        onConflict: 'id',
        ignoreDuplicates: false 
      })
      .select()
      .single();

    if (error) {
      console.error('CMS API: Error upserting team member:', error);
    } else {
      console.log('CMS API: Upserted team member successfully:', data);
    }

    return { data, error };
  },

  async deleteTeamMember(id: string) {
    console.log('CMS API: Deleting team member:', id);
    const { data, error } = await supabase
      .from('cms_team')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('CMS API: Error deleting team member:', error);
    } else {
      console.log('CMS API: Deleted team member successfully:', id);
    }

    return { data, error };
  },

  // Settings
  async getSettings() {
    console.log('CMS API: Getting all settings');
    const { data, error } = await supabase
      .from('cms_settings')
      .select('*')
      .order('key');

    if (error) {
      console.error('CMS API: Error getting settings:', error);
    } else {
      console.log('CMS API: Got settings:', data?.length || 0);
    }

    return { data, error };
  },

  async getSetting(key: string) {
    console.log('CMS API: Getting setting:', key);
    const { data, error } = await supabase
      .from('cms_settings')
      .select('*')
      .eq('key', key)
      .maybeSingle();

    if (error) {
      console.error('CMS API: Error getting setting:', key, error);
    } else {
      console.log('CMS API: Got setting:', key, data);
    }

    return { data, error };
  },

  async upsertSetting(key: string, value: any) {
    console.log('CMS API: Upserting setting:', key);
    const { data, error } = await supabase
      .from('cms_settings')
      .upsert({ 
        key, 
        value 
      }, { 
        onConflict: 'key',
        ignoreDuplicates: false 
      })
      .select()
      .single();

    if (error) {
      console.error('CMS API: Error upserting setting:', error);
    } else {
      console.log('CMS API: Upserted setting successfully:', data);
    }

    return { data, error };
  },

  // Footer Links
  async getFooterLinks() {
    console.log('CMS API: Getting footer links');
    const { data, error } = await supabase
      .from('cms_footer_links')
      .select('*')
      .order('section')
      .order('order_index', { nullsFirst: false });

    if (error) {
      console.error('CMS API: Error getting footer links:', error);
    } else {
      console.log('CMS API: Got footer links:', data?.length || 0);
    }

    return { data, error };
  },

  async upsertFooterLink(link: Partial<CmsFooterLinks>) {
    console.log('CMS API: Upserting footer link:', link.text);
    const { data, error } = await supabase
      .from('cms_footer_links')
      .upsert(link, { 
        onConflict: 'id',
        ignoreDuplicates: false 
      })
      .select()
      .single();

    if (error) {
      console.error('CMS API: Error upserting footer link:', error);
    } else {
      console.log('CMS API: Upserted footer link successfully:', data);
    }

    return { data, error };
  },

  async deleteFooterLink(id: string) {
    console.log('CMS API: Deleting footer link:', id);
    const { data, error } = await supabase
      .from('cms_footer_links')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('CMS API: Error deleting footer link:', error);
    } else {
      console.log('CMS API: Deleted footer link successfully:', id);
    }

    return { data, error };
  },
};